import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { DragDropContext } from "react-beautiful-dnd"

const COLUMN_COUNT = 2
const COLUMN_GAP = 16

/**
 * Renders a list of category cards in a Masonry-style 2-column layout that is
 * compatible with react-beautiful-dnd. MUI Masonry absolutely-positions its
 * children and reflows them whenever a card height changes (mid-drag), which
 * breaks rbd drop targets. Instead we keep two real flex columns and greedily
 * assign each card to the currently shortest column (the same balancing Masonry
 * does) so there are no inter-row gaps while staying in normal document flow.
 *
 * Each card (a Droppable + its Draggable rows) is rendered by `renderCard`.
 */
function MenuDndBoard({ items, getId, onDragEnd, renderCard }) {
  const cardRefs = useRef({})
  const [columns, setColumns] = useState([])

  const seedColumns = (list) => {
    const cols = Array.from({ length: COLUMN_COUNT }, () => [])
    ;(list || []).forEach((it, i) => cols[i % COLUMN_COUNT].push(it))
    return cols
  }

  // Render the balanced columns, falling back to a simple alternating split
  // whenever `columns` is stale (data just changed) so removed cards never paint.
  const flatColumns = columns.flat()
  const columnsMatchData =
    flatColumns.length === (items?.length || 0) &&
    flatColumns.every((it) => items?.some((m) => getId(m) === getId(it)))
  // Keep the saved arrangement but always render the freshest object for each id.
  // After a re-fetch the category ids are unchanged, so `columns` still holds the
  // stale objects — remap them to the latest `items` so updated contents show.
  const itemsById = new Map((items || []).map((it) => [getId(it), it]))
  const displayColumns = columnsMatchData
    ? columns.map((col) => col.map((c) => itemsById.get(getId(c)) || c))
    : seedColumns(items)

  // After paint, measure rendered card heights and rebalance into the shortest
  // column. Converges in two passes (heights are width-stable across columns).
  useLayoutEffect(() => {
    if (!items?.length) {
      if (flatColumns.length) setColumns([])
      return
    }
    const heights = Array.from({ length: COLUMN_COUNT }, () => 0)
    const balanced = Array.from({ length: COLUMN_COUNT }, () => [])
    items.forEach((it) => {
      const el = cardRefs.current[getId(it)]
      const h = (el?.getBoundingClientRect().height || 0) + COLUMN_GAP
      const target = heights.indexOf(Math.min(...heights))
      balanced[target].push(it)
      heights[target] += h
    })
    const signature = (cols) => cols.map((col) => col.map((c) => getId(c)).join(",")).join("|")
    if (signature(balanced) !== signature(columns)) setColumns(balanced)
  }, [items, columns])

  // Card heights depend on viewport width — re-measure (re-seed) on resize.
  useEffect(() => {
    let timer
    const onResize = () => {
      clearTimeout(timer)
      timer = setTimeout(() => setColumns([]), 150)
    }
    window.addEventListener("resize", onResize)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("resize", onResize)
    }
  }, [])

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div style={{ display: "flex", gap: COLUMN_GAP, alignItems: "flex-start" }}>
        {displayColumns.map((column, columnIndex) => (
          <div
            key={columnIndex}
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: COLUMN_GAP
            }}>
            {column.map((item) => (
              <div key={getId(item)} ref={(el) => (cardRefs.current[getId(item)] = el)}>
                {renderCard(item)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </DragDropContext>
  )
}

export default MenuDndBoard
